import { ComparePageTemplate, type CompareContent } from './ComparePageTemplate';

const content: CompareContent = {
  competitor: 'Vincere',
  competitorSegment: 'Recruitment Agency CRM / ATS (UK-based)',
  tagline:
    'Vincere is a solid agency CRM for UK and ANZ markets. GoGrabJob Business adds AI interviews, explainable scoring, and India-market support — capabilities Vincere does not offer — at a lower per-seat cost.',
  intro:
    "Vincere has earned a loyal following among mid-size recruitment agencies, particularly in the UK, Australia, and New Zealand. Its CRM depth, candidate database management, and client relationship tools are genuinely strong. But Vincere was built before AI-assisted screening became a practical possibility, and its architecture reflects that era. GoGrabJob Business was built AI-first: every job post, every application, and every interview is a structured-data event that feeds explainable scoring. For agencies that want to compete on quality, speed, and scalability, GoGrabJob Business is the more forward-looking platform.",

  tableGroups: [
    {
      groupName: 'General',
      rows: [
        { feature: 'Free trial available',          ours: 'yes',         theirs: 'partial'    },
        { feature: 'Transparent public pricing',    ours: 'yes',         theirs: 'no'         },
        { feature: 'Starting price (per seat/mo)',  ours: 'Free → ₹999', theirs: '$98+/user'  },
        { feature: 'India / INR pricing',           ours: 'yes',         theirs: 'no'         },
        { feature: 'Setup time',                    ours: '< 48 hours',  theirs: '1–3 weeks'  },
        { feature: 'Modern web app (no plugin)',    ours: 'yes',         theirs: 'yes'        },
        { feature: 'Mobile app',                    ours: 'yes',         theirs: 'yes'        },
      ],
    },
    {
      groupName: 'Agency CRM & Pipeline',
      rows: [
        { feature: 'Client relationship management',  ours: 'yes',  theirs: 'yes'      },
        { feature: 'Candidate database / CRM',        ours: 'yes',  theirs: 'yes'      },
        { feature: 'Job order management',            ours: 'yes',  theirs: 'yes'      },
        { feature: 'Placement / revenue tracking',    ours: 'yes',  theirs: 'yes'      },
        { feature: 'Candidate submission packs',      ours: 'yes',  theirs: 'yes'      },
        { feature: 'Multi-client pipelines',          ours: 'yes',  theirs: 'yes'      },
        { feature: 'Visual kanban pipeline',          ours: 'yes',  theirs: 'partial'  },
        { feature: 'Email sequences',                 ours: 'yes',  theirs: 'partial'  },
      ],
    },
    {
      groupName: 'AI & Assessment',
      rows: [
        { feature: 'AI video + voice interviews',      ours: 'yes',               theirs: 'no'    },
        { feature: 'Explainable role-fit scoring',     ours: 'Gap-level analysis', theirs: 'no'   },
        { feature: 'Automated candidate shortlisting', ours: 'yes',               theirs: 'no'    },
        { feature: 'AI resume parsing',                ours: 'yes',               theirs: 'partial'},
        { feature: 'Multilingual AI (EN/HI/Hinglish)', ours: 'yes',               theirs: 'no'    },
        { feature: 'Structured scorecard with rubric', ours: 'yes',               theirs: 'partial'},
      ],
    },
    {
      groupName: 'Workflow & Automation',
      rows: [
        { feature: 'Visual workflow automation engine', ours: 'yes',  theirs: 'partial' },
        { feature: 'Calendar & interview scheduling',   ours: 'yes',  theirs: 'yes'     },
        { feature: 'Referral management',               ours: 'yes',  theirs: 'yes'     },
        { feature: 'Compliance / right-to-work checks', ours: 'partial', theirs: 'yes'  },
        { feature: 'Timesheets & contractor management',ours: 'no',   theirs: 'yes'     },
      ],
    },
    {
      groupName: 'Reporting & Compliance',
      rows: [
        { feature: 'GDPR tooling & DPA',        ours: 'yes',  theirs: 'yes'    },
        { feature: 'Audit trail',               ours: 'yes',  theirs: 'yes'    },
        { feature: 'SSO (SAML / OIDC)',         ours: 'yes',  theirs: 'yes'    },
        { feature: 'Custom analytics reports',  ours: 'yes',  theirs: 'yes'    },
        { feature: 'API access',                ours: 'yes',  theirs: 'yes'    },
      ],
    },
  ],

  metrics: [
    { value: '4×',   label: 'Faster setup',         sub: 'GoGrabJob live in 48 hrs vs Vincere 1–3 weeks' },
    { value: '60%',  label: 'Lower per-seat cost',  sub: 'GoGrabJob vs Vincere $98+/user pricing' },
    { value: '2×',   label: 'More roles screened',  sub: 'per recruiter per week using AI interviews' },
  ],

  deepDive: [
    {
      title: 'AI interviews: where the gap is most visible',
      body: "Vincere's assessment capability is limited to structured notes and scorecard fields filled in by human interviewers. There is no AI that interviews candidates, no automated scoring, and no role-fit analysis that appears without a recruiter doing the work manually. GoGrabJob Business runs AI video and voice interviews for every applicant automatically — the moment the job goes live. Recruiters log in to find a ranked, scored shortlist instead of an unfiltered inbox. For high-volume roles where agencies need to process 100+ applicants efficiently, this difference alone justifies the switch.",
      highlight: "GoGrabJob AI screens every applicant automatically. Vincere requires a human to conduct every initial assessment.",
    },
    {
      title: 'Pricing that works for Indian agencies and global boutiques',
      body: "Vincere's per-user pricing at $98+/month makes it expensive for growing agencies. A 10-person recruitment team pays over $11,700/year — before any premium add-ons. GoGrabJob Business offers transparent per-seat pricing in both INR and USD, with plan tiers designed for teams from 1 to 1,000+ seats. Indian recruitment agencies can access the full platform at ₹999–₹4,999/month for small teams, with no currency risk or international payment friction.",
    },
    {
      title: 'Modern UX that recruiters actually want to use',
      body: "Vincere carries the UX patterns of a platform built in the 2010s: dense data views, navigation menus that require training to understand, and a configuration overhead that discourages customisation. GoGrabJob Business was designed with modern recruiter workflows in mind: a clean kanban pipeline, a candidate card with all relevant information visible at a glance, and workflow automation you configure in a visual builder without contacting support. New recruiters are productive within their first day.",
    },
    {
      title: 'India and APAC as first-class markets',
      body: "Vincere is deeply embedded in the UK and ANZ recruitment markets. Indian customers are supported but the platform was not designed with the Indian recruiting context in mind — no INR pricing, no Hindi/Hinglish AI, and data residency defaults to non-Indian regions. GoGrabJob Business was built by an Indian team for the Indian market and global operations. Data is stored in Mumbai by default. AI interviews support the code-switching communication styles common in Indian professional settings.",
    },
  ],

  ourAdvantages: [
    'AI video + voice interviews running automatically for every applicant',
    'Explainable gap-level role-fit scoring — no manual scoring required',
    'INR pricing — significantly lower cost for Indian agencies',
    'Multilingual AI: English, Hindi, and Hinglish out of the box',
    'Live in 48 hours with no lengthy implementation project',
    'Modern, clean UX that new recruiters learn without training',
    'Visual workflow automation on all paid plans',
  ],

  theirAdvantages: [
    'Deeper timesheet and contractor management for staffing firms',
    'Stronger compliance / right-to-work check tooling for UK market',
    'More established brand among UK and ANZ recruitment agencies',
    'Deeper financial reporting and placement revenue analytics',
    'Larger back-office integration ecosystem for UK payroll/finance',
  ],

  faqs: [
    {
      q: "Can I migrate our Vincere candidate database into GoGrabJob?",
      a: "Yes. GoGrabJob Business accepts CSV imports for candidates, companies, contacts, and job orders. We provide a migration checklist with field mappings from Vincere's standard export format. For larger agencies with 50,000+ candidate records, we offer a supported migration service with data validation before go-live.",
    },
    {
      q: "Does GoGrabJob have the client relationship management features we use in Vincere?",
      a: "GoGrabJob Business includes a full agency CRM: client company records, contact management, job order tracking, placement history, and revenue tracking. Email threads and call notes attach to both the client record and the candidate record. The main difference from Vincere is the AI layer on top — every candidate in the CRM can have an AI interview and role-fit score associated with them.",
    },
    {
      q: "We process high volumes of contract placements — does GoGrabJob handle that?",
      a: "GoGrabJob Business is optimised for permanent and fixed-term placement workflows. Timesheet management and contractor payroll are not current features — if this is a significant part of your billing model, you would need a specialist contractor management platform alongside GoGrabJob. For perm and retained search agencies, GoGrabJob Business covers the full workflow.",
    },
    {
      q: "How does GoGrabJob's scoring compare to Vincere's assessment tools?",
      a: "Vincere's assessment capability relies on human interviewers completing scorecard forms after conducting a screen. GoGrabJob Business runs AI interviews automatically, scoring each candidate on your job-specific competency rubric before any human is involved. The AI assessment serves as a first-pass filter that surfaces your top 10% for human review — reducing recruiter time on screening by 60–80%.",
    },
    {
      q: "Is GoGrabJob suitable for boutique specialist recruitment agencies?",
      a: "Yes. The Growth plan is popular among boutique agencies with 3–20 recruiters. You get unlimited active roles, full AI interview capability, branded submission packs, and client-scoped pipeline access for hiring managers. The interface is clean enough that both permanent consultants and contract-focused recruiters can work from the same platform without a training programme.",
    },
  ],
};

export function VsVincerePage() {
  return <ComparePageTemplate content={content} />;
}
